/*
 **  PeerTime -- Network Peer Time Synchronization
 **  Copyright (c) 2018-2019 Dr. Ralf S. Engelschall <rse@engelschall.com>
 **
 **  Permission is hereby granted, free of charge, to any person obtaining
 **  a copy of this software and associated documentation files (the
 **  "Software"), to deal in the Software without restriction, including
 **  without limitation the rights to use, copy, modify, merge, publish,
 **  distribute, sublicense, and/or sell copies of the Software, and to
 **  permit persons to whom the Software is furnished to do so, subject to
 **  the following conditions:
 **
 **  The above copyright notice and this permission notice shall be included
 **  in all copies or substantial portions of the Software.
 **
 **  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 **  EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 **  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 **  IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 **  CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 **  TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 **  SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/*
 *  ==== Synchronous Programming Utilities ====
 */

/*  sleep for a certain time  */
const sleep = (delay: number) => {
  return new Promise<void>((resolve, reject) => {
    setTimeout(() => resolve(), delay);
  });
};

/*  repeat as long as a condition holds  */
const whilst = (
  condition: () => boolean,
  callback: () => Promise<any>
): Promise<any> => {
  return new Promise<void>((resolve, reject) => {
    const recurse: () => void | Promise<any> = () =>
      condition() ? callback().then(() => recurse()) : resolve();
    recurse();
  });
};

/*
 *  ==== Statistics Utilities ====
 */

/*  the arithmetical mean  */
const mean = (arr: Array<number>) => arr.reduce((a, b) => a + b) / arr.length;

/*  the variance (squared deviation from the mean)  */
const variance = (arr: Array<number>) => {
  if (arr.length < 2) return 0;
  const _mean = mean(arr);
  return (
    arr.map((x) => Math.pow(x - _mean, 2)).reduce((a, b) => a + b) /
    (arr.length - 1)
  );
};

/*  the standard deviation (square root of the variance)  */
const std = (arr: Array<number>) => Math.sqrt(variance(arr));

/*  the median (middle value of an ordered set)  */
const median = (arr: Array<number>) => {
  if (arr.length < 2) return arr[0];
  const sorted = arr.slice().sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
  if (sorted.length % 2 === 0)
    return (sorted[arr.length / 2 - 1] + sorted[arr.length / 2]) / 2;
  else return sorted[(arr.length - 1) / 2];
};

/*
 *  ==== the Application Programming Interface (API) ====
 */

/*  the interface of a data frame  */
interface PeerTimeFrame {
  fid?: number /*  frame id  */;
  rid?: number /*  reply (frame) id  */;
  from: string /*  sender peer id */;
  to: string /*  receiver peer id */;
  type: string /*  type of frame  */;
  data?: any /*  payload of frame  */;
}

type Options = {
  id: string /*  the id of this peer  */;
  peers: string[] /*  the ids of the remote peers   */;
  interval: number /*  interval for doing automatic synchronizations (in ms)  */;
  delay: number /*  delay between requests (in ms)  */;
  repeat: number /*  number of times to do a request to one peer  */;
  now: () => number /*  return the local system time (in ms)  */;
  send: (
    frame: PeerTimeFrame
  ) => Promise<any> /*  send a frame to a remote peer  */;
};

/*  the exported API class  */
export default class PeerTime {
  options: Options;
  offset: number;
  timer: any | null;
  progress: boolean;
  first: boolean;
  fid: number;
  rpccb: { [key in string]: any };

  constructor(options: Pick<Options, "send" | "id"> & Partial<Options>) {
    /*  determine options  */
    this.options = Object.assign(
      {},
      {
        peers: [] /* the ids of the remote peers  */,
        interval:
          60 *
          60 *
          1000 /* interval for doing automatic synchronizations (in ms) */,
        timeout: 10000 /* timeout for requests to fail (in ms) */,
        delay: 1000 /* delay between requests (in ms) */,
        repeat: 5 /* number of times to do a request to one peer */,
        now: () => Date.now() /* return the local system time (in ms) */,
      },
      options
    );

    /*  initialize time offset  */
    this.offset = 0;

    /*  initialize interval timer and progress flag  */
    this.timer = null;
    this.progress = false;
    this.first = true;

    /*  initialize RPC facility  */
    this.fid = 0;
    this.rpccb = {};
  }

  /*  return the identifier of this peer  */
  id() {
    return this.options.id;
  }

  /*  receive the datagram of a peer  */
  receive(frame: PeerTimeFrame) {
    /*  process frame  */
    if (typeof frame.rid === "number") {
      /*  frame is a response/reply  */
      if (!(frame.rid in this.rpccb))
        throw new Error("unexpected frame type (invalid reply id)");
      /*  deferred execution of RPC callback  */
      this.rpccb[frame.rid](frame);
    } else if (frame.type === "TIME-REQ") {
      /*  frame is a request  */
      /*  reply with local time  */
      const reply = {
        fid: this.fid++,
        rid: frame.fid,
        from: this.id(),
        to: frame.from,
        type: "TIME-RES",
        data: this.now(),
      };
      try {
        this.options.send(reply);
      } catch (err) {
        /* no-op */
      }
    } else throw new Error("received unexpected frame (invalid frame type)");
  }

  /*  trigger a synchronization with all peers  */
  async sync() {
    /*  perform a request/response operation pair  */
    const _rpc = (frame: PeerTimeFrame) => {
      return new Promise<PeerTimeFrame>((resolve, reject) => {
        /*  determine new unique frame id  */
        const fid = this.fid++;

        /*  provide callback for deferred response handling  */
        this.rpccb[fid] = (frame: PeerTimeFrame) => {
          /*  cleanup callback  */
          delete this.rpccb[fid];

          /*  cleanup frame  */
          frame = Object.assign({}, frame);
          delete frame.fid;
          delete frame.rid;

          /*  return frame as the result  */
          resolve(frame);
        };

        /*  send request frame  */
        let result;
        try {
          /*  add frame id  */
          frame = Object.assign({}, frame, { fid });

          /*  send the frame  */
          result = this.options.send(frame);
        } catch (err) {
          /*  catch synchronous errors  */
          reject(err);
        }
        if (result === undefined)
          throw new Error("send callback has to return a Promise");
        result.catch((err) => {
          /*  catch asynchronous errors  */
          reject(err);
        });
      });
    };

    /*  determine time offset for a single peer via RPC  */
    const _getOffset = (peer: string) => {
      /*  determine start time  */
      const start = this.options.now();

      /*  create and send request frame  */
      const frame = {
        from: this.id(),
        to: peer,
        type: "TIME-REQ",
      };
      return _rpc(frame)
        .then((frame) => {
          /*  determine end time and roundtrip duration  */
          const end = this.options.now();
          const roundtrip = end - start;

          /*  determine remote peer time and local peer offset  */
          const timestamp = frame.data;
          const offset = Math.trunc(timestamp - end + roundtrip / 2);

          /*  apply the first ever retrieved offset immediately  */
          if (this.first) {
            this.first = false;
            if (this.offset !== offset) {
              this.offset = offset;
            }
          }

          /*  return the retrieved information  */
          return { peer, roundtrip, offset };
        })
        .catch((/* err */) => {
          /*  just mark error cases  */
          return null;
        });
    };

    /*  perform a synchronization with a single peer  */
    const _syncWithPeer = (peer: string) => {
      /*  assemble all offsets  */
      const all: Array<{
        peer: string;
        roundtrip: number;
        offset: number;
      } | null> = [];

      /*  perform a single synchronization operation  */
      const sync = () => _getOffset(peer).then((result) => all.push(result));

      /*  repeat the synchroniation operation multiple times  */
      return _getOffset(peer)
        .then(() => {
          return whilst(
            () => all.length < this.options.repeat,
            () => sleep(this.options.delay).then(sync)
          );
        })
        .then(() => {
          /*  filter out results marked as error  */
          const results = all.filter((result) => result !== null);

          /*  calculate the limit for outliers  */
          const roundtrips = results.map((result) => result!.roundtrip);
          const limit = median(roundtrips) + std(roundtrips);

          /*  filter all results which have a roundtrip smaller or equal than the limit  */
          const limited = results.filter(
            (result) => result!.roundtrip <= limit
          );
          const offsets = limited.map((result) => result!.offset);

          /*  return the mean from the limited offsets  */
          return offsets.length > 0 ? mean(offsets) : null;
        });
    };

    /*  perform synchronization with all peers  */
    return Promise.all(
      this.options.peers.map((peer) => _syncWithPeer(peer))
    ).then((all) => {
      /*  filter out results marked as error or which are invalid numbers  */
      const offsets = all.filter(
        (offset) => offset !== null && !isNaN(offset) && isFinite(offset)
      );

      /*  pick the mean of all peer offsets  */
      if (offsets.length > 0) {
        const newOffset = Math.trunc(mean(offsets as any));
        if (this.offset !== newOffset) {
          this.offset = newOffset;
        }
      }
    });
  }

  /*  start automatic synchronization  */
  start() {
    this.stop();
    this.timer = setInterval(async () => {
      if (this.progress) return;
      this.progress = true;
      await this.sync();
      this.progress = false;
    }, this.options.interval);
  }

  /*  stop automatic synchronization  */
  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  /*  return the peer-aligned "global" time  */
  now() {
    let now = Math.trunc(this.options.now());
    now += this.offset;
    return now;
  }
}
